import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Input, Select, Tag, Space, Button, Row, Col, Statistic, Typography, Tooltip, Badge, Drawer, Descriptions } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, AppstoreOutlined, ToolOutlined, StarOutlined, TrophyOutlined, FireOutlined, GiftOutlined, ThunderboltOutlined, RocketOutlined } from '@ant-design/icons';
import { itemAPI } from '../services/api';

const { Text } = Typography;
const { Option } = Select;

const ITEM_CATEGORIES_MAP = {
  item: '道具',
  equip: '装备',
  hero: '英雄',
  fighter_eqp: '战机装备',
  decorate: '装饰',
  trial_item: '试炼道具',
  pubg_Item: '吃鸡道具',
  newgvg_Item: 'GVG道具'
};

function ItemManagement() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const { current: pageCurrent, pageSize } = pagination;

  const loadCategories = useCallback(async () => {
    try {
      const response = await itemAPI.getCategories();
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Load categories error:', error);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pageCurrent,
        limit: pageSize
      };
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (searchKeyword) {
        params.keyword = searchKeyword;
      }
      const response = await itemAPI.getList(params);
      if (response.data.success) {
        setItems(response.data.data.items);
        setPagination(prev => ({ ...prev, total: response.data.data.total }));
      }
    } catch (error) {
      console.error('Load items error:', error);
    } finally {
      setLoading(false);
    }
  }, [pageCurrent, pageSize, searchKeyword, selectedCategory]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getCategoryColor = (category) => {
    const colors = {
      '道具': 'blue',
      '装备': 'green',
      '英雄': 'purple',
      '战机装备': 'orange',
      '装饰': 'cyan',
      '试炼道具': 'volcano',
      '吃鸡道具': 'red',
      'GVG道具': 'geekblue'
    };
    return colors[category] || 'default';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      '道具': <GiftOutlined />,
      '装备': <ToolOutlined />,
      '英雄': <TrophyOutlined />,
      '战机装备': <RocketOutlined />,
      '装饰': <StarOutlined />,
      '试炼道具': <FireOutlined />,
      '吃鸡道具': <ThunderboltOutlined />,
      'GVG道具': <AppstoreOutlined />
    };
    return icons[category] || null;
  };

  const getQualityColor = (quality) => {
    const colors = {
      1: 'default',
      2: 'green',
      3: 'blue',
      4: 'purple',
      5: 'orange',
      6: 'red'
    };
    return colors[quality] || 'default';
  };

  const getQualityText = (quality) => {
    const texts = {
      1: '普通',
      2: '优秀',
      3: '稀有',
      4: '史诗',
      5: '传说',
      6: '神话'
    };
    return texts[quality] || `${quality}星`;
  };

  const columns = [
    {
      title: '物品ID',
      dataIndex: 'item_id',
      key: 'item_id',
      width: 100,
      sorter: (a, b) => a.item_id - b.item_id,
      render: (id) => <Text copyable={{ text: String(id) }} strong>{id}</Text>
    },
    {
      title: '物品名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (name) => <span style={{ fontWeight: 'bold' }}>{name || '未命名'}</span>
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category) => (
        <Tag color={getCategoryColor(category)} icon={getCategoryIcon(category)}>
          {category}
        </Tag>
      )
    },
    {
      title: '品质',
      dataIndex: 'quality',
      key: 'quality',
      width: 80,
      render: (quality) => quality ? <Tag color={getQualityColor(quality)}>{getQualityText(quality)}</Tag> : '-'
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type) => type !== undefined && type !== null ? <Tag>{type}</Tag> : '-'
    },
    {
      title: '等级数',
      key: 'levels',
      width: 80,
      render: (_, record) => record.hasMultipleLevels ? <Badge count={record.levels.length} style={{ backgroundColor: '#52c41a' }} /> : (record.levels.length > 0 ? '1' : '-')
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="查看详情">
          <Button size="small" icon={<EyeOutlined />} onClick={() => { setCurrentItem(record); setDetailVisible(true); }} />
        </Tooltip>
      )
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic title="物品总数" value={pagination.total} prefix={<AppstoreOutlined />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="分类数量" value={categories.length} prefix={<ToolOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="当前显示" value={items.length} prefix={<EyeOutlined />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="当前分类" value={selectedCategory === 'all' ? '全部' : (ITEM_CATEGORIES_MAP[selectedCategory] || selectedCategory)} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Select
              value={selectedCategory}
              onChange={(value) => { setSelectedCategory(value); setPagination(prev => ({ ...prev, current: 1 })); }}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="all">全部分类</Option>
              {categories.map(cat => (
                <Option key={cat.key} value={cat.key}>{cat.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Input.Search
              placeholder="搜索物品：输入 ID 或名称"
              onSearch={handleSearch}
              prefix={<SearchOutlined />}
              size="large"
              allowClear
            />
          </Col>
          <Col span={4}>
            <Button onClick={loadItems} icon={<ReloadOutlined />} size="large">刷新</Button>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Space>
              <Text type="secondary">共 {pagination.total} 个物品</Text>
            </Space>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="item_id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 个，共 ${total} 个物品`,
          pageSizeOptions: ['20', '50', '100', '200']
        }}
        onChange={(pag) => setPagination(pag)}
        size="middle"
        scroll={{ x: 1200 }}
      />

      <Drawer
        title="物品详情"
        placement="right"
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
        width={600}
      >
        {currentItem && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="物品ID">{currentItem.item_id}</Descriptions.Item>
            <Descriptions.Item label="物品名称">{currentItem.name || '未命名'}</Descriptions.Item>
            <Descriptions.Item label="分类">
              <Tag color={getCategoryColor(currentItem.category)} icon={getCategoryIcon(currentItem.category)}>
                {currentItem.category}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="品质">
              {currentItem.quality ? <Tag color={getQualityColor(currentItem.quality)}>{getQualityText(currentItem.quality)}</Tag> : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="类型">{currentItem.type !== undefined && currentItem.type !== null ? currentItem.type : '-'}</Descriptions.Item>
            {currentItem.pos && <Descriptions.Item label="装备部位">{currentItem.pos}</Descriptions.Item>}
            {currentItem.rank && <Descriptions.Item label="装备品阶">{currentItem.rank}</Descriptions.Item>}
            {currentItem.fightPower && <Descriptions.Item label="战斗力">{currentItem.fightPower}</Descriptions.Item>}
            <Descriptions.Item label="描述">{currentItem.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="等级数">{currentItem.levels.length}</Descriptions.Item>
            {currentItem.levels.length > 0 && (
              <Descriptions.Item label="等级列表">
                <Space wrap>
                  {currentItem.levels.map((lvl, idx) => (
                    <Tag key={idx} color={getQualityColor(lvl.quality)}>
                      Lv.{lvl.level} {lvl.quality ? `(${getQualityText(lvl.quality)})` : ''}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
}

export default ItemManagement;
